// documentClassification.js
// Helper functions for managing document-level classification

import {CLASSIFICATION_LEVELS} from './classificationLevels';
import {blockClassificationLevel, blockClassificationTokens} from '../../../utils/documentHeaderMeta';
import {getReferencesForMark} from '../yMaps/referencesYMap';

/**
 * Get the priority/rank of a classification level
 * Higher number = more classified
 */
export function getClassificationRank(level) {
    const index = CLASSIFICATION_LEVELS.findIndex(l => l.value === level);
    return index >= 0 ? index : 0;
}

/**
 * Compare two classification levels
 * Returns: 1 if level1 > level2, -1 if level1 < level2, 0 if equal
 */
export function compareClassificationLevels(level1, level2) {
    const rank1 = getClassificationRank(level1);
    const rank2 = getClassificationRank(level2);
    if (rank1 > rank2) return 1;
    if (rank1 < rank2) return -1;
    return 0;
}

/**
 * Get the maximum classification level from two levels
 */
export function getMaxClassificationLevel(level1, level2) {
    return compareClassificationLevels(level1, level2) >= 0 ? level1 : level2;
}

/**
 * MINSKY-17131: true when a classification level is more restrictive than (U).
 * Unknown/missing levels rank as (U) (rank 0) and are therefore not "above U".
 */
export function isAboveU(level) {
    return getClassificationRank(level) > 0;
}

/**
 * Calculate the maximum classification level in the document
 * by traversing all paragraphs and headings
 */
export function calculateDocumentMaxClassification(doc) {
    let maxLevel = 'U';

    doc.descendants((node) => {
        if (node.type.name === 'paragraph' || node.type.name === 'heading') {
            const nodeLevel = blockClassificationLevel(node.attrs.classificationLevel);
            maxLevel = getMaxClassificationLevel(maxLevel, nodeLevel);
        }
    });

    return maxLevel;
}

/**
 * MINSKY: compute the highest classification level among all citations (reference marks)
 * attached to a single paragraph/heading block, so callers can enforce it as a floor —
 * a block can never be set to a level lower than its most-classified citation.
 *
 * @param {import('prosemirror-model').Node} blockNode - the paragraph/heading node
 * @param {import('yjs').Map|null|undefined} yReferenceMarks - Y.Map("referenceMarks")
 * @returns {string} highest classification level among the block's citations, or 'U' if none
 */
export function getBlockCitationsMaxLevel(blockNode, yReferenceMarks) {
    let maxLevel = 'U';
    if (!blockNode || !yReferenceMarks) return maxLevel;

    const uids = new Set();
    blockNode.descendants((node) => {
        if (!node.isText) return;
        node.marks.forEach((mark) => {
            if (mark.type.name === 'reference' && mark.attrs.uid) {
                uids.add(mark.attrs.uid);
            }
        });
    });

    uids.forEach((uid) => {
        const refs = getReferencesForMark(yReferenceMarks, uid);
        refs.forEach((ref) => {
            const refLevel = ref.docClassificationLevel ?? ref.classificationLevel ?? 'U';
            maxLevel = getMaxClassificationLevel(maxLevel, refLevel);
        });
    });

    return maxLevel;
}

/**
 * Collect all unique distribution values from the document
 */
export function collectDocumentDistributions(doc) {
    const distributions = new Set();

    doc.descendants((node) => {
        if (node.type.name === 'paragraph' || node.type.name === 'heading') {
            blockClassificationTokens(node.attrs.distribution, 'distribution')
                .forEach((token) => distributions.add(token));
        }
    });

    return Array.from(distributions);
}

/**
 * Collect all unique compartment values from the document
 */
export function collectDocumentCompartments(doc) {
    const compartments = new Set();

    doc.descendants((node) => {
        if (node.type.name === 'paragraph' || node.type.name === 'heading') {
            blockClassificationTokens(node.attrs.compartment, 'compartment')
                .forEach((token) => compartments.add(token));
        }
    });

    return Array.from(compartments);
}
